import type { SupabaseClient } from '@supabase/supabase-js';
import { YYYY_MM_DD_REGEX } from '@/utils/date/calendarDate';
import { getNowInTimezone, getOrgTimezone } from '@/utils/cron/invoiceCronHelpers';

/**
 * First class date for an Ongoing Class (#4724).
 *
 * The date lives on `classes.first_class_date` and is CASCADED into every weekly
 * `class_schedules.effective_date` by the class API on save (an undated class
 * keeps each slot's stored start instead, #6688). Every "does this
 * class meet on date X" surface already honours that window, so nothing here is
 * read by a meeting surface — these helpers only decide what the API stamps and
 * whether a given date is before the first class.
 *
 * All dates are 'YYYY-MM-DD' strings compared lexically. Every `today` /
 * `gymToday` here is the GYM-local date (getNowInTimezone(getOrgTimezone(..)));
 * the class routes resolve it once per save via gymLocalToday below.
 */

export function normalizeFirstClassDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return YYYY_MM_DD_REGEX.test(trimmed) ? trimmed : null;
}

export interface ResolveOngoingScheduleStartInput {
  /** classes.first_class_date — wins when set. */
  firstClassDate: string | null | undefined;
  /** class_sections.session_start_date the form sent back for this section (ongoing classes only). */
  sectionSessionStartDate: string | null | undefined;
  /** Gym-local today, 'YYYY-MM-DD'. */
  today: string;
  /** #6688: the start this slot keeps from its stored row (keptWeeklySlotStarts). */
  keptStart?: string | null;
}

/**
 * What an ongoing class's WEEKLY schedule rows get as `effective_date`.
 * The first class date wins over the section date; with neither, the start
 * the slot keeps from before the save, and only then today. A past first
 * class date is stamped as-is — it is history, not an error.
 */
export function resolveOngoingScheduleStart(input: ResolveOngoingScheduleStartInput): string {
  const firstClassDate = normalizeFirstClassDate(input.firstClassDate);
  if (firstClassDate) return firstClassDate;
  const sectionStart = normalizeFirstClassDate(input.sectionSessionStartDate);
  if (sectionStart) return sectionStart;
  const kept = normalizeFirstClassDate(input.keptStart);
  if (kept && kept <= input.today) return kept;
  return input.today;
}

/**
 * True when an ongoing slot's start would fall back past both dates, so the
 * class route must read the stored rows before it deletes them.
 */
export function ongoingStartNeedsStoredRows(input: {
  firstClassDate: string | null | undefined;
  sectionSessionStartDate: string | null | undefined;
}): boolean {
  return !normalizeFirstClassDate(input.firstClassDate) && !normalizeFirstClassDate(input.sectionSessionStartDate);
}

export interface StoredScheduleRow {
  day_of_week: unknown;
  start_time: unknown;
  effective_date: unknown;
  end_date: unknown;
  is_active: unknown;
}

export interface IncomingWeeklySlot {
  day_of_week: unknown;
  start_time: unknown;
}

function slotTime(value: unknown): string {
  return typeof value === 'string' ? value.slice(0, 5) : '';
}

/**
 * #6688: the class route deletes and reinserts a section's schedule on every
 * save. Before this, every weekly row of an undated ongoing class restarted on
 * the save day, so the first-month count skipped the month's earlier dates (a
 * Tuesday class saved Nov 20 read "1 of 1" and a family joining paid a full
 * month for one class).
 *
 * Returns, per incoming WEEKLY slot (same order), the stored start it keeps, or
 * null for "a new slot: starts on the save day". A stored row lends its start
 * to ONE slot, in this order:
 *   1. same weekday and start time (nothing changed);
 *   2. same weekday (the time moved);
 *   3. any weekday left over (the class moved to a new day, or one weekday
 *      was swapped for another) — the month's count keeps its real size, so a
 *      mid-month move never bills a full month for the dates after the move.
 * A slot added while every stored row is already claimed starts today, so the
 * days it never ran are not counted. Only open-ended, active
 * weekly rows lend a start (never an Added Class Date, never a row with an end
 * date), and a start after `today` is not kept.
 */
export function keptWeeklySlotStarts(
  slots: IncomingWeeklySlot[],
  storedRows: StoredScheduleRow[] | null | undefined,
  today: string,
): Array<string | null> {
  const pool = (storedRows || [])
    .filter((row) => row.is_active !== false && row.end_date == null)
    .map((row) => ({
      day: row.day_of_week,
      time: slotTime(row.start_time),
      start: normalizeFirstClassDate(row.effective_date),
    }))
    .filter((row): row is { day: unknown; time: string; start: string } => Boolean(row.start) && row.start! <= today)
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

  const kept: Array<string | null> = slots.map(() => null);
  const matchers: Array<(slot: IncomingWeeklySlot, row: (typeof pool)[number]) => boolean> = [
    (slot, row) => row.day === slot.day_of_week && row.time === slotTime(slot.start_time),
    (slot, row) => row.day === slot.day_of_week,
    () => true,
  ];
  for (const matches of matchers) {
    slots.forEach((slot, i) => {
      if (kept[i] !== null) return;
      const at = pool.findIndex((row) => matches(slot, row));
      if (at === -1) return;
      kept[i] = pool[at].start;
      pool.splice(at, 1);
    });
  }
  return kept;
}

/**
 * What an ongoing class's SECTION row gets as `session_start_date` on save.
 * The section date is a second, narrower bound inside the proration meeting
 * counter and the drop-in gates, so it must never sit LATER than the first
 * class date — otherwise the calendar (which reads the cascaded weekly row)
 * and billing would disagree. Earlier-or-equal section dates are kept; with
 * no first class date the section date passes through untouched.
 */
export function resolveOngoingSectionStart(input: {
  firstClassDate: string | null | undefined;
  sectionSessionStartDate: string | null | undefined;
}): string | null {
  const first = normalizeFirstClassDate(input.firstClassDate);
  const section = normalizeFirstClassDate(input.sectionSessionStartDate);
  if (!first) return section;
  if (!section) return null;
  return section > first ? first : section;
}

/** Gym-local today for the org, 'YYYY-MM-DD'. */
export async function gymLocalToday(supabase: SupabaseClient, organizationId: string): Promise<string> {
  const tz = await getOrgTimezone(supabase, organizationId);
  return getNowInTimezone(tz).dateStr;
}

/**
 * Today at the CLASS's location, 'YYYY-MM-DD' — the clock the public catalog
 * and detail page use for "Starts <date>" (schedule-location timezone). The
 * checkout's "is this class still ahead" must agree with what the family just
 * read, so it asks the same clock; a two-timezone gym otherwise disagrees for
 * a few hours around midnight on the eve of the first class (PR 1 carry-over).
 * With no location, or a location without a timezone, falls back to the org
 * clock (`gymLocalToday`). Billing MONTHS stay on the org clock — that is the
 * clock the monthly run keeps.
 */
export async function classLocalToday(
  supabase: SupabaseClient,
  organizationId: string,
  locationId: string | null | undefined
): Promise<string> {
  if (locationId) {
    const { data } = await supabase
      .from('gymlocations')
      .select('timezone')
      .eq('id', locationId)
      .eq('organization_id', organizationId)
      .maybeSingle();
    const tz = typeof data?.timezone === 'string' && data.timezone ? data.timezone : null;
    if (tz) return getNowInTimezone(tz).dateStr;
  }
  return gymLocalToday(supabase, organizationId);
}

/**
 * A one-off Added Class Date (a slot whose effective_date === end_date) that
 * falls before the first class date. The form refuses these; the API refuses
 * them too so a raw or stale PUT cannot persist a meeting the catalog says
 * has not started. Returns the offending date or null.
 */
export function oneOffSlotBeforeFirstClass(
  slots: Array<{ effective_date?: unknown; end_date?: unknown }> | null | undefined,
  firstClassDate: string | null | undefined
): string | null {
  const first = normalizeFirstClassDate(firstClassDate);
  if (!first || !slots) return null;
  for (const slot of slots) {
    const start = normalizeFirstClassDate(slot.effective_date);
    const end = normalizeFirstClassDate(slot.end_date);
    if (start && end && start === end && start < first) return start;
  }
  return null;
}

/** True when `dateStr` falls before the class's first class date (no date ⇒ false). */
export function isBeforeFirstClass(
  dateStr: string,
  firstClassDate: string | null | undefined
): boolean {
  const first = normalizeFirstClassDate(firstClassDate);
  if (!first) return false;
  return dateStr < first;
}

/**
 * The date the catalog should say the class "Starts" — only while the first
 * class is still ahead of gym-local today. After that, nothing (R4/R10).
 */
export function upcomingFirstClassDate(
  firstClassDate: string | null | undefined,
  gymToday: string
): string | null {
  const first = normalizeFirstClassDate(firstClassDate);
  if (!first) return null;
  return first > gymToday ? first : null;
}

/**
 * Column value the class API writes. Session Programs never carry it — the
 * CHECK constraint (`is_session_based IS NOT TRUE`) refuses it — so flipping a
 * dated ongoing class to Session Program clears it instead of failing the
 * save. Open Gym is cleared here in application code only; the constraint
 * does not mention is_open_gym. While the feature flag is off nothing is
 * written, so production rows stay NULL whatever a client sends.
 */
export function firstClassDateForWrite(input: {
  firstClassDate: unknown;
  isSessionBased: boolean;
  isOpenGym: boolean;
  featureEnabled: boolean;
}): string | null {
  if (!input.featureEnabled || input.isSessionBased || input.isOpenGym) return null;
  return normalizeFirstClassDate(input.firstClassDate);
}
